import { backOff } from 'exponential-backoff'

import { context, propagation } from '@opentelemetry/api'

// Three attempts at this ceiling, plus their backoff delays, must stay under CloudFront's 30s
// OriginReadTimeout: past that the origin is guaranteed to lose the race and the viewer gets a 504
// instead of an error we can render [S20-595]. 9500ms is the largest value that still fits.
const MAX_SERVER_FETCH_TIMEOUT_MS = 9_500

// Defined further down as a hoisted declaration: sort-exports wants exports alphabetical, so it
// cannot sit above baseFetch even though this line calls it during module init.
const SERVER_FETCH_TIMEOUT_MS = resolveServerFetchTimeout(process.env.SONICJOBS_SERVER_FETCH_TIMEOUT_MS)

// This function enhances the provided request initialization options
// with W3C trace context propagation headers.
function withW3CPropagation(init?: RequestInit): RequestInit {
  const output: { traceparent: string; tracestate: string } = { traceparent: '', tracestate: '' }
  propagation.inject(context.active(), output)
  const { traceparent, tracestate } = output
  return {
    ...init,
    headers: {
      ...init?.headers,
      traceparent,
      tracestate,
    },
  }
}

export const baseFetch = async (input: RequestInfo, init?: RequestInit): Promise<Response> => {
  // Exponential backoff algorithm:
  // https://en.wikipedia.org/wiki/Exponential_backoff
  const isServer = typeof window === 'undefined'

  return await backOff(
    async () => {
      if (isServer) {
        // A fresh signal per attempt: one created outside backOff would already have fired by
        // attempt 2, silently collapsing three attempts into one. `init` is spread afterwards so
        // a caller that passes its own signal still wins.
        return await fetch(input, withW3CPropagation({ signal: AbortSignal.timeout(SERVER_FETCH_TIMEOUT_MS), ...init }))
      }
      return await fetch(input, init)
    },
    isServer
      ? // Without jitter every pod retries on the same schedule and the herd stays synchronised.
        { maxDelay: 500, startingDelay: 50, numOfAttempts: 3, jitter: 'full' }
      : // The browser deliberately keeps the library defaults. The incident is server-side, and
        // how many times a user's own request retries is a product decision, not a resilience fix.
        { maxDelay: 500, startingDelay: 50 },
  )
}

// Clamped rather than trusted: this is an operator-set value, and a mistyped one would either
// restore the origin race or — when negative or non-finite — make AbortSignal.timeout throw
// synchronously on every attempt, failing every request instead of only the slow ones.
export function resolveServerFetchTimeout(raw?: string): number {
  return Math.min(Math.max(Math.trunc(Number(raw) || 8000), 1), MAX_SERVER_FETCH_TIMEOUT_MS)
}

export const textFetch = async (input: RequestInfo, init?: RequestInit): Promise<string | undefined> => {
  return baseFetch(input, init).then((a) => a?.text())
}
